import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  OnInit,
  SimpleChanges,
  OnChanges,
  AfterViewChecked
} from '@angular/core';
import equal from 'fast-deep-equal';
import { combineLatest, Observable, BehaviorSubject, switchMap } from 'rxjs';
import { map, takeUntil, take, distinctUntilChanged } from 'rxjs/operators';

import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { Category } from 'idt-ecommerce-api/catalog/categories/categories.models';
import { Product } from 'idt-ecommerce-api/catalog/products/products.models';
import { ProductsService } from 'idt-ecommerce-api/catalog/products/products.service';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { CMSEvent, CMSWysiwigProductEvent } from 'render/models/cms-event.model';
import { RenderEvent } from 'render/render-events/render-events.model';
import { RenderEventsService } from 'render/render-events/render-events.service';
import { CMSRenderEventsService } from 'render/services/cms-render-events.service';
import { L10nTranslationsService } from 'render/services/l10n-translations/l10n-translations.service';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { TenantVariablesService } from 'render/services/tenant-variables/tenant-variables.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { IdtComponent } from 'shared/models/idt-components/idt-component.models';
import { AssetsSharedService } from 'shared/services/assets-shared.service';
import { Link } from 'shared/services/links.model';
import { LinksService } from 'shared/services/links.service';

import { styleUrls } from './render-wysiwig.component.paths';


@Component({
  selector: 'render-wysiwyg',
  template: `
    <!-- eslint-disable @angular-eslint/template/no-call-expression -->
    <div
      #wysiwygText
      [ngStyle]="getStyles()"
      *ngIf="interpolatedHtml$ | async as innerHTML"
      [innerHTML]="innerHTML | idtTrustHtml">
    </div>
  `,
  styleUrls,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RenderWysiwygComponent
  extends UnsubscribeComponent
  implements AfterViewChecked, OnInit, OnChanges {

  @Input() public country: string | undefined;
  @Input() public lang: string | undefined;
  @Input() public html: string | undefined;
  @Input() public piece: IdtComponent | undefined;

  @Input() public textPaddingTop: string = '0%';
  @Input() public textPaddingBottom: string = '0%';
  @Input() public textPaddingLeft: string = '0%';
  @Input() public textPaddingRight: string = '0%';

  @ViewChild('wysiwygText', { static: false }) public wysiwygText!: ElementRef<HTMLElement>;

  public interpolatedHtml$: Observable<string> = new Observable((observer) => observer.next(''));

  private readonly html$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private readonly categoryPlaceholderRegExp = /{{category\.[0-9]+\.(\w)+}}/g;
  private readonly categoryWithoutSectionPathRegExp = /\/c[0-9]+\.html+/g;
  private readonly productPlaceholderRegExp = /{{product\.[0-9]+\.(\w)+}}/g;
  private readonly tenantPlaceholderRegExp = /{{tenant\.(\w)+}}/g;
  private readonly l10nPlaceholderRegExp = /{{l10n\.[\w.-]+}}/g;
  private readonly products: Product[] = [];

  constructor(
    private readonly contextService: ContextService,
    private readonly tenantVariablesService: TenantVariablesService,
    private readonly l10nTranslationsService: L10nTranslationsService,
    private readonly renderEventsService: RenderEventsService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly productsService: ProductsService,
    private readonly assetsSharedService: AssetsSharedService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly linksService: LinksService
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.html) {
      this.html$.next(changes.html.currentValue as string | undefined ?? '');
    }
  }

  public ngOnInit(): void {
    this.fixParagraphLineHeight();
    this.getProductsInformationFromPlaceholders();
    this.initInterpolation();
  }

  public getStyles(): Record<string, string> {
    return {
      'padding-block-start': this.toUnit(this.textPaddingTop),
      'padding-block-end': this.toUnit(this.textPaddingBottom),
      'padding-inline-start': this.toUnit(this.textPaddingLeft),
      'padding-inline-end': this.toUnit(this.textPaddingRight)
    };
  }

  public ngAfterViewChecked(): void {
    const element = this.wysiwygText?.nativeElement;
    const links = element?.querySelectorAll('a') || [];

    if (links.length > 0) {
      const regExp = /^(?:[a-z]+:)?\/\//i;
      Array.prototype.forEach.call(links, (domLink: HTMLLinkElement) => {
        const type = domLink.getAttribute('data-link-type') ?? '';
        const src = domLink.getAttribute('href')!;
        const isRelativeLink = src ? !regExp.test(src) : undefined;
        const isNewWindow = domLink.getAttribute('target') === '_blank';
        const isEnableUnifiedUrl = domLink.getAttribute('data-enable-unified-url') === 'true';

        if (type === 'category' || type === 'product') {
          const link: Link = {
            type,
            categoryId: domLink.getAttribute('data-category-id') ?? '',
            productId: domLink.getAttribute('data-product-id') ?? '',
            src,
            enableUnifiedUrl: isEnableUnifiedUrl
          };
          this.linksService.getLinkUrl(link).pipe(take(1)).subscribe((href) => {
            domLink.setAttribute('href', href ?? src);
            this.setOnClickEvent(domLink, type, href ?? src, isRelativeLink, isNewWindow);
          });
        } else {
          if (type === 'file') {
            domLink.setAttribute(
              'href',
              this.assetsSharedService.getAssetUrl(src, this.renderConfigurationService.config.assetsUrl) ?? src
            );
          }
          this.setOnClickEvent(domLink, type, src, isRelativeLink, isNewWindow);
        }
      });
    }
  }

  private setOnClickEvent(
    domLink: HTMLLinkElement,
    type: string,
    src: string,
    isRelativeLink: boolean | undefined,
    isNewWindow: boolean
  ): void {
    domLink.onclick = (event: Event) => {
      const categoryId = domLink.getAttribute('data-category-id')!;
      const productId = domLink.getAttribute('data-product-id')!;
      const section = domLink.getAttribute('data-section')!;
      const slug = domLink.getAttribute('data-slug')!;
      const identifier = domLink.getAttribute('data-identifier')!;
      const location = this.piece?.name ?? '';
      const isEnableUnifiedUrl = domLink.getAttribute('data-enable-unified-url') === 'true';

      const renderEvent: RenderEvent = {
        event,
        extra: {
          type,
          categoryId,
          productId,
          src,
          section,
          slug,
          identifier,
          relativeLink: isRelativeLink,
          newWindow: isNewWindow,
          enableUnifiedUrl: isEnableUnifiedUrl
        },
        location
      };
      this.renderEventsService.events.next(renderEvent);

      const product = this.products.find((p) => p.id === productId);
      const cmsEvent: CMSEvent<CMSWysiwigProductEvent> = {
        name: 'productLinkClicked',
        type: 'click',
        data: {
          type,
          categoryId,
          product: {} as Product,
          src,
          slug,
          section,
          location,
          identifier,
          relativeLink: !!isRelativeLink,
          newWindow: isNewWindow,
          enableUnifiedUrl: isEnableUnifiedUrl
        }
      };
      if (product) {
        cmsEvent.data.product = product;
        this.cmsRenderEventsService.emitEvent(cmsEvent);
      } else {
        this.productsService.productDetailOrNull(productId, categoryId === '' ? undefined : categoryId).pipe(
          take(1)
        ).subscribe((p) => {
          cmsEvent.data.product = p;
          this.cmsRenderEventsService.emitEvent(cmsEvent);
        });
      }
    };
  }

  private initInterpolation(): void {
    this.interpolatedHtml$ = combineLatest([
      this.html$,
      this.contextService.getCategories$(),
      this.productsService.getProducts$(),
      this.tenantVariablesService.getTenantVariables$(),
      this.l10nTranslationsService.getL10nTranslations$()
    ]).pipe(
      distinctUntilChanged(equal),
      map(([html, categories, products, tenantVariables, l10nTranslations]:
      [string, Category[], Record<string, Product>, Record<string, string>, Record<string, string>]) => {
        let result = html;
        if (this.hasPlaceholders(result)) {
          result = this.interpolateL10nTranslations(result, l10nTranslations);
          result = this.interpolateTenantVariables(result, tenantVariables);
          result = this.interpolateCategoriesAndProducts(result, categories, Object.values(products));
        }
        if (this.categoryWithoutSectionPathRegExp.test(result)) {
          result = this.addCategorySectionPath(categories, result);
        }
        return result;
      })
    );
  }

  private addCategorySectionPath(categories: Category[], url: string): string {
    const categoriesWithoutSectionPath = url.match(this.categoryWithoutSectionPathRegExp)!;
    for (const match of categoriesWithoutSectionPath) { // 'match' is something like /c1010281508.html
      const categoryId = match.replace('/c', '').replace('.html', '');
      const category = CategoriesHelpers.category(categories, categoryId);
      const categorySectionPath = category ? category.sectionPath : '';
      if (categorySectionPath !== '') {
        url = url.replace(match, `/${categorySectionPath}-c${categoryId}.html`);
      }
    }
    return url;
  }

  private fixParagraphLineHeight(): void {
    // FIXME: https://axinic.central.inditex.grp/jira/browse/MECFRONT-2193
    this.interpolatedHtml$
      .pipe(
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe(() => {
        const el = this.wysiwygText?.nativeElement;
        if (el) {
          const paragraphs = el.querySelectorAll('p');
          for (let i = 0; i < paragraphs.length; i++) {
            const spans = paragraphs[i].querySelectorAll('span');
            for (let j = 0; j < spans.length; j++) {
              if (spans[j].style.lineHeight !== '') {
                paragraphs[i].style.lineHeight = '0';
              }
            }
          }
        }
      });
  }

  private hasPlaceholders(text: string): boolean {
    return this.categoryPlaceholderRegExp.test(text)
      || this.productPlaceholderRegExp.test(text)
      || this.tenantPlaceholderRegExp.test(text)
      || this.l10nPlaceholderRegExp.test(text);
  }

  private interpolateL10nTranslations(html: string, translations: Record<string, string>): string {
    return html.replace(this.l10nPlaceholderRegExp, (match) => {
      const key = match.slice('{{l10n.'.length, -2);
      return translations[key] ?? '';
    });
  }

  private interpolateTenantVariables(html: string, tenantVariables: Record<string, string>): string {
    return html.replace(this.tenantPlaceholderRegExp, (match: string) => {
      const splitted = match.substr(0, match.length - 2).substr(2).split('.');
      return tenantVariables[splitted[1]] ?? '';
    });
  }

  private interpolateCategoriesAndProducts(html: string, categories: Category[], products: Product[]): string {
    return html.replace(this.categoryPlaceholderRegExp, (match: string) => {
      const splitted = match.substr(0, match.length - 2).substr(2).split('.');
      const value = CategoriesHelpers.category(categories, splitted[1])?.[splitted[2] as 'name' | 'description'];
      return value ?? '';
    }).replace(this.productPlaceholderRegExp, (match: string) => this.interpolateProducts(products, match));
  }

  private interpolateProducts(products: Product[], match: string): string {
    const placeholderElements = match.substring(0, match.length - 2).substring(2).split('.');
    const productKeyToInterpolate = placeholderElements[2] as 'name' | 'description' | 'price'
      | 'oldPrice' | 'localePrice' | 'oldLocalePrice';
    const product = products.find((p: Product) => p.id === placeholderElements[1]);

    if (!product) {
      return '';
    }

    switch (productKeyToInterpolate) {
      case 'name':
        return product.name;
      case 'description':
        return product.longDescription ?? '';
      case 'price':
        return product.priceInfo?.price?.toString() ?? '';
      case 'oldPrice':
        return product.priceInfo?.oldPrice?.toString() ?? '';
      case 'localePrice':
        return product.priceInfo?.localePrice?.toString() ?? '';
      case 'oldLocalePrice':
        return product.priceInfo?.oldLocalePrice?.toString() ?? '';
      default:
        return '';
    }
  }

  private toUnit(value: number | string): string {
    return (typeof value === 'number') ? `${value}%` : value;
  }

  private getProductsInformationFromPlaceholders(): void {
    this.html$.pipe(
      takeUntil(this.clearSubscriptions$),
      switchMap((html: string) => {
        const productsIds = this.getProductsIdsFromPlaceholders(html);
        return this.productsService.storeInformationOfProducts$(productsIds);
      })
    ).subscribe();
  }

  private getProductsIdsFromPlaceholders(html: string): string[] {
    const productsMatch = html.match(this.productPlaceholderRegExp);
    const productIds = (productsMatch ?? []).map((match: string) => match.substring(0, match.length - 2).substring(2).split('.')[1]);

    return [...new Set(productIds)];
  }

}
